import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SmsAutoReplySettings } from 'infrastructure/classes/sms-autoreply-settings';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';

import type { ISmsAutoReplySettings } from 'infrastructure/interfaces';

const smsAutoReplySettingsApi = 'core-2/automated-sms-settings';
const QUERY_KEY = 'sms-autoreply-settings';

type UseSmsAutoReplySettings = {
  smsAutoReplySettings: Array<SmsAutoReplySettings>;
  loading: boolean;
  updateSmsAutoReplySettings: (data: {
    key: string;
    available: boolean;
  }) => Promise<void>;
};

export const useSmsAutoReplySettings = (): UseSmsAutoReplySettings => {
  const queryFn = async () => {
    const { data, error } = await dataService.getList<ISmsAutoReplySettings[]>(
      smsAutoReplySettingsApi,
    );
    if (error) {
      showResult(error);
      throw new Error(error);
    }
    return data?.map((el) => new SmsAutoReplySettings(el));
  };

  const {
    data: smsAutoReplySettings = [],
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn,
  });

  const queryClient = useQueryClient();

  const [isSettingsLoading, setIsSettingsLoading] = useState(false);

  const updateSmsAutoReplySettings = async (
    setting: {
      key: string;
      available: boolean;
    },
    onSuccess?: () => void,
  ) => {
    setIsSettingsLoading(true);
    const { error, data } = await dataService.updateList<{
      data: { success: boolean };
    }>(smsAutoReplySettingsApi, setting);
    setIsSettingsLoading(false);
    if (error) {
      showResult(error);
      return;
    }
    if (data) {
      if (onSuccess) {
        onSuccess();
      }
      const newItems = smsAutoReplySettings.map((item) => {
        if (item.key === setting.key) {
          return { ...item, available: setting.available };
        }
        return item;
      });
      queryClient.setQueryData([QUERY_KEY], newItems);
    }
  };

  return {
    smsAutoReplySettings,
    loading: isLoading || isRefetching || isSettingsLoading,
    updateSmsAutoReplySettings,
  };
};
